import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { RootStoreContext } from '../../global/storeContext';
import { Path, useLocation } from 'react-router-dom';
import { UserManagementMode } from 'realhaus-sdk';
import { paths } from '../../routes';
import { Grid, Link, Typography } from '@material-ui/core';
import { BlankLayout } from '../../layouts/misc/blank';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { ResetPassword } from '../../components/auth/acct/actions';

const getQueryParamValue = (param: string, location: Path) => {
    return new URLSearchParams(location.search).get(param);
};

export enum PasswordResetState {
    CODE_UNVERIFIED,
    CODE_VERIFIED,
    SUBMITTING,
    COMPLETE,
}

export const AuthActionPage: React.FC = observer(() => {
    const { uiStore, userStore } = React.useContext(RootStoreContext);
    const [actionSuccessful, setActionSuccessful] = React.useState<boolean | null>(null);
    const [mode] = React.useState(
        (getQueryParamValue('mode', useLocation()) as UserManagementMode) ?? null,
    );
    const [actionCode] = React.useState(getQueryParamValue('oobCode', useLocation()));
    const [continueUrl] = React.useState(
        getQueryParamValue('continueUrl', useLocation()) ?? paths.home,
    );
    const [email] = React.useState(getQueryParamValue('email', useLocation()));

    const [opComplete, setOpComplete] = React.useState(false);

    const [passwordResetState, setPasswordResetState] = useState<PasswordResetState>(
        PasswordResetState.CODE_UNVERIFIED,
    );
    const [passwordResetErrorMsg, setPasswordResetErrorMsg] = React.useState<string>();

    const init = async () => {
        if (!!mode && !!actionCode) {
            switch (mode) {
                case UserManagementMode.VERIFY_EMAIL: {
                    // handle verify email
                    const verified = await userStore.verifyEmail(actionCode);
                    setActionSuccessful(verified ?? false);
                    if (verified) {
                        if (email) {
                            // sendWelcomeEmail can take a while, no need to wait for response
                            userStore.sendWelcomeEmail(email);
                        }
                        if (continueUrl) {
                            setTimeout(() => {
                                uiStore.goTo(continueUrl);
                            }, 3000);
                        }
                    }
                    break;
                }
                case UserManagementMode.RECOVER_EMAIL: {
                    break;
                }
                case UserManagementMode.RESET_PASSWORD: {
                    const verified = await userStore.verifyPasswordReset(actionCode);
                    setActionSuccessful(verified ?? false);
                    if (verified) setPasswordResetState(PasswordResetState.CODE_VERIFIED);

                    break;
                }
                default:
                    break;
            }
        }
    };

    useEffect(() => {
        if (!mode || !actionCode) {
            setOpComplete(true);
            return;
        }

        uiStore.showLoading();

        init()
            .catch((err) => {})
            .finally(() => {
                setOpComplete(true);
                uiStore.hideLoading();
            });
    }, [mode, actionCode]);

    const isVerifyEmailAction = mode === UserManagementMode.VERIFY_EMAIL;
    const isResetPasswordAction = mode === UserManagementMode.RESET_PASSWORD;

    const RedirectMsg = () => (
        <Typography variant='body1'>
            You will be redirected shortly. If you are not redirected in a few seconds,{' '}
            <Link href={continueUrl} target='_self'>
                click here
            </Link>
        </Typography>
    );

    const ErrorIcon = () => (
        <ErrorOutlineOutlinedIcon color='error' fontSize='large' style={{ marginRight: '5px' }} />
    );

    const AcctVerifyMsg = () =>
        opComplete && isVerifyEmailAction ? (
            <>
                {actionSuccessful === true && (
                    <>
                        <Typography variant='h4'>Your email has been verified!</Typography>
                        <RedirectMsg />
                    </>
                )}
                {actionSuccessful === false && (
                    <>
                        <Typography
                            variant='h4'
                            style={{
                                verticalAlign: 'middle',
                                marginTop: '10px',
                                marginBottom: '10px',
                            }}
                        >
                            <ErrorIcon />
                            Try verifying your email again
                        </Typography>
                        <br />
                        <Typography variant='body1'>
                            Your request to verify your email has expired or the link has already
                            been used.
                        </Typography>
                    </>
                )}
            </>
        ) : (
            <></>
        );

    const handleSubmitNewPassword = (newPassword: string) => {
        uiStore.showLoading();
        setPasswordResetState(PasswordResetState.SUBMITTING);
        userStore
            .resetPassword(actionCode!!, newPassword)
            .then((v) => {
                if (!v) {
                    setPasswordResetErrorMsg('Failed to update Password. Try again later');
                    setPasswordResetState(PasswordResetState.CODE_VERIFIED);
                } else {
                    setPasswordResetState(PasswordResetState.COMPLETE);
                }
            })
            .catch((err) => {
                console.error(err);
                setPasswordResetErrorMsg('Failed to update Password. Try again later');
                setPasswordResetState(PasswordResetState.CODE_VERIFIED);
            })
            .finally(() => {
                uiStore.hideLoading();
            });
    };

    const AcctResetPassword = () => (
        <>
            {opComplete && isResetPasswordAction && actionSuccessful ? (
                passwordResetState === PasswordResetState.CODE_VERIFIED ||
                passwordResetState === PasswordResetState.SUBMITTING ? (
                    <Grid item spacing={3}>
                        <ResetPassword
                            onResetPassword={handleSubmitNewPassword}
                            errorMsg={passwordResetErrorMsg}
                            state={passwordResetState}
                        />
                    </Grid>
                ) : passwordResetState === PasswordResetState.COMPLETE ? (
                    <Grid item spacing={3}>
                        <Typography variant='h4' style={{ paddingTop: '8px' }}>
                            Your password has been successfully changed!
                        </Typography>
                    </Grid>
                ) : null
            ) : null}
        </>
    );

    const InvalidModeMsg = () =>
        opComplete &&
        (mode === null || (!isVerifyEmailAction && !isResetPasswordAction) || !actionSuccessful) ? (
            <>
                <Grid item spacing={3}>
                    <Typography variant='h1' style={{ fontWeight: 'bold', fontSize: '8rem' }}>
                        Oops!
                    </Typography>

                    <Typography variant='h2' style={{ paddingTop: '8px' }}>
                        {(mode === null || (!isVerifyEmailAction && !isResetPasswordAction)) &&
                            'The specified mode is invalid!'}
                        {!actionSuccessful && 'The specified code is invalid!'}
                    </Typography>
                </Grid>
            </>
        ) : (
            <></>
        );

    return (
        <BlankLayout>
            {!uiStore.loading && (
                <>
                    <InvalidModeMsg />
                    <AcctVerifyMsg />
                    <AcctResetPassword />
                </>
            )}
        </BlankLayout>
    );
});
